import { z } from 'zod'
import {
  assetConditionSchema,
  assetMediaSchema,
  buildingTypeSchema,
} from '../assets/assetDataTypes.ts'

export const extractedListingSchema = z.object({
  building_type: buildingTypeSchema,
  asking_price: z.number().nullish().default(0).describe('Debt-free asking price in euros'),
  final_price: z
    .number()
    .nullish()
    .describe('Final price in euros. Only used for sold properties. Set null if not applicable.'),
  listing_date: z.string().nullish().describe('Date the property was listed.'),
  sale_date: z.string().nullish().describe('Date the property was sold.'),
  rooms: z.number().optional().default(1),
  floor_count: z
    .number()
    .nullish()
    .default(1)
    .describe('Number of floors inside the property or unit.'),
  on_floor: z
    .number()
    .nullish()
    .describe('Floor number the property is on, if applicable (only for apartments).'),
  square_meters: z.number().nullish().describe('Total square meters of the property.'),
  lot_square_meters: z.number().nullish().describe('Square meters of the lot, if applicable.'),
  lot_ownership: z
    .enum(['own', 'lease', 'unknown'])
    .nullish()
    .default('unknown')
    .describe('Ownership of the lot, if applicable.'),
  build_year: z.string().nullish().describe('Year the property was built.'),
  condition: assetConditionSchema.nullish(),
  renovations: z
    .array(
      z.object({
        year: z.string(),
        description: z.string(),
      })
    )
    .nullish()
    .describe('Renovations done to the property.'),
  address: z
    .object({
      unit_number: z.string().optional(),
      street: z.string().min(1),
      street_number: z.string(),
      postal_code: z.string(),
      city: z.string(),
      country: z.string().default('Finland'),
    })
    .nullish()
    .describe(
      'Address of the property. This is crucial information and should never be left empty unless no address can be assigned. If country is not provided, set it to Finland. Always use the English name for the country, such as "Finland" instead of "Suomi".'
    ),
  description: z.string().nullish().describe('Description of the property.'),
  images: z
    .array(z.string())
    .nullish()
    .describe(
      'URLs of images of the property, up to 4. All images must be related to the property. Ensure all urls are valid.'
    ),
})

export type ExtractedListingType = z.infer<typeof extractedListingSchema>

export const parsedListingSchema = extractedListingSchema
  .omit({
    images: true,
  })
  .extend({
    images: z.array(assetMediaSchema).nullish().default([]),
  })

export type ParsedListingType = z.infer<typeof parsedListingSchema>

export const extractedListingUpdateSchema = z.object({
  asking_price: z.number().nullish(),
  images: z.array(z.string()).nullish(),
  listing_date: z.string().nullish(),
  sale_date: z.string().nullish(),
})

export type ExtractedListingUpdateType = z.infer<typeof extractedListingUpdateSchema>

export const parsedListingUpdateSchema = parsedListingSchema.pick({
  images: true,
  asking_price: true,
  listing_date: true,
  sale_date: true,
})

export type ParsedListingUpdateType = z.infer<typeof parsedListingUpdateSchema>

export type IngestPropertyResponse<ResultType> =
  | {
      status: 'completed'
      data?: ResultType
    }
  | {
      status: 'failed'
      error: string
      message?: string
    }

export const assetTaskStatusSchema = z.enum(['COMPLETED', 'FAILED', 'PENDING', 'IDLE', 'UNKNOWN'])

export type AssetTaskStatus = z.infer<typeof assetTaskStatusSchema>

export const workflowRunStatusSchema = z.enum([
  'completed',
  'failed',
  'cancelled',
  'pending',
  'running',
  'paused',
])

export type WorkflowRunStatus = z.infer<typeof workflowRunStatusSchema>

const workflowStatusToAssetStatus: Record<WorkflowRunStatus, AssetTaskStatus> = {
  completed: 'COMPLETED',
  failed: 'FAILED',
  cancelled: 'IDLE',
  pending: 'PENDING',
  running: 'PENDING',
  paused: 'PENDING',
}

export function mapWorkflowStatus(workflowStatus: string): AssetTaskStatus {
  const parsed = workflowRunStatusSchema.safeParse(workflowStatus)

  if (!parsed.success) {
    return 'UNKNOWN'
  }

  return workflowStatusToAssetStatus[parsed.data]
}
